import React, { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getErrorMessage } from '../../packages/api-client';
import { PageHeader, Badge, StateView, Table } from '../../packages/ui';
import type { Column } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
    BREADCRUMB, GroupsPermissionsTabs, fetchPermissions, groupByResource, resourceLabel, type Permission,
} from './RolesPage';
import './RolesPage.css';

type Row = Permission & Record<string, unknown>;

const COLUMNS: Column<Row>[] = [
    { key: 'code', header: 'Identifier', rowHeader: true, render: (p) => <code className="gp-code">{p.resource}:{p.action}</code> },
    { key: 'description', header: 'Description', render: (p) => p.description || '—' },
    { key: 'resource', header: 'Resource', render: (p) => resourceLabel(p.resource) },
    { key: 'roles', header: 'Granted to', render: (p) => (p.roles?.length ? p.roles.join(', ') : 'No role') },
];

/** Read-only permission catalogue (GET /permissions). Grants are edited on the role catalogue. */
export function PermissionsPage() {
    usePageTitle('Permission catalogue');
    const searchId = useId();
    const resourceId = useId();
    const [query, setQuery] = useState('');
    const [resource, setResource] = useState('');

    const permsQ = useQuery({ queryKey: ['permissions'], queryFn: fetchPermissions });
    const all = permsQ.data ?? [];
    const resources = useMemo(() => groupByResource(permsQ.data ?? []).map(([r]) => r), [permsQ.data]);

    const q = query.trim().toLowerCase();
    const rows = all.filter((p) => (!resource || p.resource === resource)
        && (!q || `${p.resource}:${p.action} ${p.description}`.toLowerCase().includes(q))) as Row[];

    return (
        <div className="gp-page">
            <PageHeader
                breadcrumb={BREADCRUMB}
                title="Permission catalogue"
                subtitle="Every resource:action capability the server checks. Grants are changed on the global role catalogue."
                actions={<Badge variant="info">Read-only</Badge>}
            />
            <GroupsPermissionsTabs />

            {permsQ.error ? (
                <StateView state="error" message={getErrorMessage(permsQ.error)}
                    onRetry={() => permsQ.refetch()} retrying={permsQ.isFetching} />
            ) : permsQ.isLoading ? (
                <StateView state="loading" label="Loading permissions" />
            ) : all.length === 0 ? (
                <StateView state="empty" title="No permissions in the catalogue" />
            ) : (
                <>
                    <div className="gp-filters" role="search">
                        <div className="form-group">
                            <label htmlFor={searchId} className="form-label">Search</label>
                            <input id={searchId} className="form-input" type="search" value={query}
                                placeholder="Identifier or description" onChange={(e) => setQuery(e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor={resourceId} className="form-label">Resource</label>
                            <select id={resourceId} className="form-input" value={resource}
                                onChange={(e) => setResource(e.target.value)}>
                                <option value="">All resources</option>
                                {resources.map((r) => <option key={r} value={r}>{resourceLabel(r)}</option>)}
                            </select>
                        </div>
                    </div>
                    {rows.length === 0 ? (
                        <StateView state="filtered-empty" onClearFilters={() => { setQuery(''); setResource(''); }} />
                    ) : (
                        <Table columns={COLUMNS} data={rows} rowKey={(p) => p.id} density="compact"
                            caption={`${rows.length} of ${all.length} permissions`} />
                    )}
                </>
            )}
        </div>
    );
}
